import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownRight, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { RevealText } from '../motion/RevealText';

export const Hero = () => {
    const navigate = useNavigate();
    return (
        <section
            id="top"
            data-testid="msd-hero"
            className="relative min-h-screen w-full flex items-end px-6 md:px-10 pb-20 md:pb-28 z-20 pointer-events-none"
        >
            <div className="mx-auto max-w-[1600px] w-full grid grid-cols-12 gap-4 md:gap-10">
                {/* Left column — headline */}
                <div className="col-span-12 md:col-span-8 pointer-events-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.4, delay: 0.3 }}
                        className="mb-6 flex items-center gap-4 flex-wrap"
                    >
                        <span className="w-10 h-px bg-adobe-500" />
                        <span className="eyebrow text-bone-200">01 — Est. Rio Rancho · Serving all of New Mexico</span>
                        <span className="eyebrow text-adobe-500">◆ Veteran Owned & Operated</span>
                    </motion.div>

                    <RevealText
                        el="h1"
                        lines={["Transform", "your home."]}
                        delay={0.5}
                        className="font-display font-black uppercase tracking-[-0.03em] leading-[0.86] text-bone-100 text-[15vw] md:text-[10vw] lg:text-[9.4rem] xl:text-[11rem]"
                    />
                    <span className="sr-only" aria-label="Transform your home">Transform your home.</span>
                    <motion.p
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 1.6 }}
                        className="mt-10 max-w-xl font-body text-lg md:text-xl font-light text-bone-200 leading-relaxed"
                        data-testid="hero-subhead"
                    >
                        Delivering premium residential, commercial, and industrial painting services with
                        <span className="text-bone-100"> unmatched craftsmanship, reliability, and attention to detail</span> across Rio Rancho, Albuquerque, and all of New Mexico.
                    </motion.p>

                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 2.0 }}
                        className="mt-12 flex flex-wrap items-center gap-4"
                    >
                        <button
                            type="button"
                            onClick={() => navigate('/estimate')}
                            className="btn-pill btn-pill--solid"
                            data-testid="hero-cta-primary"
                        >
                            Get a free estimate
                            <ArrowDownRight size={16} strokeWidth={1.5} />
                        </button>
                        <a
                            href="#work"
                            className="btn-pill btn-pill--ghost"
                            data-testid="hero-cta-secondary"
                        >
                            View our work
                        </a>
                        <button
                            type="button"
                            onClick={() => navigate('/paintvision')}
                            className="btn-pill btn-pill--ghost"
                            data-testid="hero-cta-paintvision"
                        >
                            See your room in new colors — free AI preview
                        </button>
                    </motion.div>

                    {/* Trust badges */}
                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 2.3 }}
                        className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3"
                        data-testid="hero-trust-badges"
                    >
                        {['Licensed', 'Bonded', 'Insured', 'Veteran-Owned'].map((label, i) => (
                            <li
                                key={label}
                                className="flex items-center gap-2 font-mono text-[10px] tracking-[0.32em] uppercase text-bone-300"
                            >
                                <span className={`w-1.5 h-1.5 rounded-full ${i === 3 ? 'bg-brand-red' : 'bg-brand-blue'}`} />
                                {label}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                {/* Right column — meta */}
                <motion.aside
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 2.2 }}
                    className="col-span-12 md:col-span-4 md:pl-10 md:border-l md:border-white/10 pointer-events-auto self-end flex flex-col gap-6"
                >
                    <div>
                        <div className="eyebrow mb-2">Coordinates</div>
                        <div className="font-mono text-xs text-bone-200 flex items-center gap-2">
                            <MapPin size={12} strokeWidth={1.5} className="text-adobe-500" />
                            35.2325° N · 106.6630° W
                        </div>
                    </div>
                    <div>
                        <div className="eyebrow mb-2">Chapter</div>
                        <div className="font-display text-3xl text-brand-red leading-none">I. Arrival</div>
                        <div className="font-body text-sm text-bone-400 mt-2">
                            The house begins slightly weathered. Scroll to renew it.
                        </div>
                    </div>
                </motion.aside>
            </div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 2.4 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-30"
            >
                <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-bone-400">Scroll</div>
                <div className="w-px h-10 bg-gradient-to-b from-bone-400/60 to-transparent" />
            </motion.div>
        </section>
    );
};
